import React, { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useMeasure, useToggle, useWindowSize } from 'react-use';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Grid } from "@mui/material";
import { useQueryParams } from 'use-query-params';
import { ActivityIndicator, AppBar, EmptyState, Page } from 'components';
import { FILTER } from 'constants/actions';
import { Tune as TuneIcon } from "@mui/icons-material";
import { useTranslation, useFiltersCount } from 'hooks';
import { useCascadeList } from 'hooks/api/cascade';
import { cascadeStore } from 'stores';
import { useAbortRequest } from 'utils/useAbortRequest';
import { CascadeList, CascadeListFilters } from './components';
import {
  defaultFilterValues,
  defaultQueryParams,
} from './CascadeListScreen.mock';

const CascadeListScreen = observer(() => {
  const { t } = useTranslation();
  const { height: pageHeight } = useWindowSize();
  const [appBarRef, { height: appBarHeight }] = useMeasure();
  const listHeight = pageHeight - appBarHeight;
  const listRef = useRef(null);
  const history = useHistory();
  const { path } = useRouteMatch();
  const formRef = useRef(null);
  const [isFilterDialogOpened, toggleFilterDialog] = useToggle(false);
  const [queryParams, setQueryParams] = useQueryParams(defaultQueryParams);
  const { ...filters } = queryParams;
  const appliedFiltersCount = useFiltersCount(filters);
  const { abortSignal } = useAbortRequest();
  const {
    isFetching,
    isError,
    isSuccess,
    data: cascadeList,
  } = useCascadeList(filters, abortSignal);

  const actions = [
    {
      id: FILTER,
      title: t(FILTER),
      icon: TuneIcon,
      show: 'always',
      badge:
        appliedFiltersCount === 0
          ? null
          : { badgeContent: appliedFiltersCount, color: 'primary' },
    },
  ];

  const handleItemSelect = (entityId) => {
    history.push(`${path}/${entityId}`);
  };

  const handleFiltersClose = () => {
    toggleFilterDialog();
  };

  const handleSubmit = (formData) => {
    setQueryParams({ ...formData });
    if (listRef.current) {
      cascadeStore.prevScroll = 0;
    }
    handleFiltersClose();
  };

  const handleFiltersReset = () => {
    if (formRef.current) {
      formRef.current.setValues(defaultFilterValues);
    }
  };

  const handleActionItemSelected = (item) => {
    switch (item) {
      case FILTER:
        toggleFilterDialog();
        break;
      default:
        break;
    }
  };

  const handleBackClick = () => {
    history.goBack();
  };

  const renderFilterDialog = () => {
    return (
      <CascadeListFilters
        ref={formRef}
        filters={filters}
        open={isFilterDialogOpened}
        onClose={handleFiltersClose}
        onApply={handleSubmit}
        onReset={handleFiltersReset}
      />
    );
  };

  const renderEmptyState = () => {
    return (
      <EmptyState title={`${t('noResults')} ${t('tryChangeSearchParams')}`} />
    );
  };

  const renderList = () => {
    const hasData = cascadeList && cascadeList.length > 0;

    if (!hasData) {
      return renderEmptyState();
    }

    return (
      <CascadeList
        data={cascadeList}
        ref={listRef}
        listHeight={listHeight}
        onItemSelect={handleItemSelect}
      />
    );
  };

  return (
    <Page>
      <Grid container direction="column">
        <AppBar
          ref={appBarRef}
          title={t('cascade')}
          actions={actions}
          actionsIconSx={{ ml: 1.5 }}
          onActionSelected={handleActionItemSelected}
          onNavClick={handleBackClick}
          navIcon="back"
        />
        {isFetching && <ActivityIndicator />}
        <Grid item xs>
          {isSuccess && renderList()}
          {isError && renderEmptyState()}
        </Grid>
      </Grid>
      {renderFilterDialog()}
    </Page>
  );
});

export default CascadeListScreen;
